import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { differenceInSeconds, format, parseISO } from "date-fns";

import QueryBuilderTwoToneIcon from '@mui/icons-material/QueryBuilderTwoTone';
import DownloadingIcon from '@mui/icons-material/Downloading';
import DescriptionTwoToneIcon from '@mui/icons-material/DescriptionTwoTone';
import CancelTwoToneIcon from '@mui/icons-material/CancelTwoTone';
import WarningTwoToneIcon from '@mui/icons-material/WarningTwoTone';
import CheckCircleTwoToneIcon from '@mui/icons-material/CheckCircleTwoTone';

import { withAuthentication } from "auth";
import { FetchJob, JobStatus, JobStep } from "types";
import {
  fetchJobStatuses
} from "api/callbacks";
import { TableColumn, TableDataRow } from "types/table";
import { handleTableAction } from "utils/helpers/handleTableAction";
import { ActionTable } from "components/Table";
import { Box, LinearProgress, SvgIconProps, Tooltip } from "@mui/material";
import ConfirmationForm from "components/Forms/ConfirmationForm";
import jobStatusEventEmitter from "components/JobStatusEventEmitter";

const SyncLogs = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [jobStatuses, setJobStatuses] = useState<JobStatus[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selected, setSelected] = useState<JobStatus[]>([]);

  const loadJobStatuses = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data: fetchedJobStatuses } = await fetchJobStatuses([], 200);
      setJobStatuses(fetchedJobStatuses);
    } catch (error) {
      console.error("Failed to fetch job statuses", error);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    loadJobStatuses();
  }, [loadJobStatuses]);

  useEffect(() => {
    const unsubscribe = jobStatusEventEmitter.subscribe((update) => {
      // receive updates from the SignalR channel in AppBar
      const jobStatus = update;
      setJobStatuses((current) => {
        const index = current.findIndex((js) => js.id === jobStatus.id);
        if (index > -1) {
          const updatedStatuses = [...current];
          updatedStatuses[index] = jobStatus;
          return updatedStatuses;
        } else {
          return [jobStatus, ...current];
        }
      });

    });
    return () => unsubscribe();
  }, []);
  
  const renderStatus = (icon: React.ReactElement<SvgIconProps>, textKey: string, statusMessage: string) => (
    <Box sx={{ display: 'flex', justifyContent: 'left', alignItems: 'center' }}>
      <Tooltip title={t(textKey)} arrow>
        <span>{icon}</span>
      </Tooltip>
      <Box sx={{marginLeft: '1em'}}>{statusMessage}</Box>
    </Box>
  );

  const getStatusMessage = (jobStatus: JobStatus) => {
    if (jobStatus.step === JobStep.Queued) {
      return renderStatus(
        <QueryBuilderTwoToneIcon fontSize="small" />,
        "synclogs:queued",
        jobStatus.statusMessage
      );
    }

    if (jobStatus.step === JobStep.Fetch) {
      return renderStatus(
        <DownloadingIcon fontSize="small" color="info" />,
        "synclogs:fetching",
        jobStatus.statusMessage
      );
    }

    if (jobStatus.step === JobStep.Transformation) {
      return renderStatus(
        <DescriptionTwoToneIcon fontSize="small" color="info" />,
        "synclogs:transforming",
        jobStatus.statusMessage
      );
    }

    if (jobStatus.hasError) {
      return renderStatus(
        <CancelTwoToneIcon fontSize="small" color="error" />,
        "synclogs:cancelled",
        jobStatus.statusMessage
      );
    }

    if (jobStatus.fetchJobs.some(job => job.hasError)) {
      return renderStatus(
        <WarningTwoToneIcon fontSize="small" color="primary" />,
        "synclogs:partially_successfull",
        jobStatus.statusMessage
      );
    }

    return renderStatus(
      <CheckCircleTwoToneIcon fontSize="small" color="success" />,
      "synclogs:done",
      jobStatus.statusMessage
    );
  };

  const getPercentage = (jobStatus: JobStatus) => {
    if (jobStatus.step === JobStep.Done && !jobStatus.hasError) {
      return 100;
    }
    const fetchJobsCount = jobStatus.fetchJobs.length;
    if (jobStatus.step === JobStep.Transformation) {
      const fetchPercentageDone = (fetchJobsCount/(fetchJobsCount+1)) * 100;
      return (jobStatus.transformationPercentage/(fetchJobsCount+1)) + fetchPercentageDone;
    }

    const fetchpercentage = jobStatus.fetchJobs.reduce(
      (acc, job) => acc + job.percentage,
      0
    );
    return fetchpercentage / (fetchJobsCount+1);
  };

  const getProgress = (jobStatus: JobStatus) => {
    const percentage = getPercentage(jobStatus);
    const color = jobStatus.hasError ? "error"
      : jobStatus.fetchJobs.some(job => job.hasError) ? "primary"
      : jobStatus.step === JobStep.Done ? "success"
      : "info";
    
      return <LinearProgress color={color} variant="determinate" value={percentage} />
  }

  const columns = useMemo(
    (): TableColumn[] => [
      { name: "started", label: t("synclogs:started"), width: 2 },
      { name: "client", label: t("synclogs:client"), width: 2 },
      { name: "connections_count", label: t("synclogs:connections_count"), width: 1 },
      { name: "total_time", label: t("synclogs:total_time"), width: 1 },
      { name: "sync_type", label: t("synclogs:sync_type"), width: 1 },
      { name: "progress", label: t("synclogs:progress"), width: 1 },
      { name: "status", label: t("synclogs:status"), width: 3 },
    ],
    [t]
  );

  const data = useMemo(
    (): TableDataRow<JobStatus>[] =>
      jobStatuses.map((job) => ({
        item: job,
        hover: true,
        sx: { cursor: "pointer" },
        // onClick: () => navigate(`/organisations/${organisation.id}`),
        values: [
          {
            name: "started",
            value: format(parseISO(job.started),"yyyy-MM-dd HH:mm:ss"),
          },
          {
            name: "client",
            value: job.clientName,
          },
          {
            name: "connections_count",
            value: job.fetchJobs.length,
          },
          {
            name: "total_time",
            value: (() => {
              const updates : string[] = [job.updated, ...job.fetchJobs.map(x => x.updated || '')];
              updates.sort().reverse();
              const latestUpdate = parseISO(updates[0]);
              const startTime = parseISO(job.started);
              const s = differenceInSeconds(latestUpdate, startTime);
              return `${Math.floor(s / 60)}m${s % 60}s`;
            })(),
          },
          {
            name: "sync_type",
            value: job.isFullSync ? t("synclogs:full_sync") : t("synclogs:delta_sync"),
          },
          {
            name: "progress",
            value: getProgress(job)
          },
          {
            name: "status",
            value: getStatusMessage(job),
          },
        ],
      })),
    [jobStatuses, navigate]
  );

  const onSelect = useCallback((item: JobStatus) => {
    const itemId = item.id;
    setSelected((prevState) =>
      prevState.some((selectedItem) => selectedItem.id === itemId)
        ? prevState.filter((selectedItem) => selectedItem.id !== itemId)
        : [...prevState, item]
    );
  }, []);

  const rowDetailColumns = useMemo(
    (): TableColumn[] => [
      { name: "id", label: t("synclogs:connection_id"), width: 2},
      { name: "name", label: t("synclogs:connection_name"), width: 2},
      { name: "progress", label: t("synclogs:progress"), width: 1 },
      { name: "started", label: t("synclogs:started"), width: 2 },
      { name: "total_time", label: t("synclogs:total_time"), width: 2 },
    ],
    [t]
  );

  const rowDetailData = (jobstatus: JobStatus): TableDataRow<FetchJob>[] =>
    jobstatus.fetchJobs.map((job) => ({
      item: job,
      sx: { cursor: "pointer" },
      values: [
        {
          name: "id",
          value: job.connectionId,
        },
        {
          name: "name",
          value: job.connectionName
        },
        {
          name: "progress",
          value: (() => {
            const color = job.hasError ? "error"
              : job.percentage === 100 ? "success"
              : "info";
            
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <LinearProgress
                    color={color}
                    variant="determinate"
                    value={job.percentage}
                    sx={{ flexGrow: 1}}
                  />
                  <Box sx={{ marginLeft: '8px', minWidth: '3.8em', textAlign: 'right', fontVariantNumeric: 'tabular-nums', paddingRight: '1em' }}>
                    {`${Math.round(job.percentage)}%`}
                  </Box>
                </Box>
              );
          })()
        },
        {
          name: "started",
          value: job.started && format(parseISO(job.started), "yyyy-MM-dd HH:mm:ss"),
        },
        {
          name: "total_time",
          value: (() => {
            if (!job.started) return '';
            const latestUpdate = parseISO(job.updated || '');
            const startTime = parseISO(job.started);
            const s = differenceInSeconds(latestUpdate, startTime);
            return `${Math.floor(s / 60)}m${s % 60}s`;
          })(),
        },
      ],
  }));

  const renderRowDetails = (job: JobStatus) => (
    <ActionTable<FetchJob>
      columns={rowDetailColumns}
      data={rowDetailData(job)}
      selectable={false}
      sx={{
        fontSize: 'small',
        '& .MuiTableCell-root': { fontSize: 'small', padding: '12px'}, // alla celler
        '& .MuiTableCell-head': { padding: '12px' }, // headerceller
        '& .MuiButtonBase-root': { fontSize: 'small' },
      }}
    />
  );

  const disabledStates = useMemo(
    () => ({
      delete: 'wip',
    }),
    [selected, t]
  );

  const actions = useMemo(
    () => ({
    }),
    [t, isLoading]
  );

  const handleDelete = useCallback(
      async (closeDialog?: () => void) => {
        if (selected.length !== 1) return;
        const client = selected[0];
        /*await handleTableAction({
          apiCall: () => {console.log('test')},
          successMessage: t("alert:delete_success", {
            type: t("client:client"),
            name: 'test',
          }),
          failureMessage: t("alert:delete_fail", {
            type: t("client:client"),
            name: 'test',
          }),
          closeDialog,
          callback: loadJobStatuses,
        });*/
        setSelected([]);
      },
      [selected, t, loadJobStatuses]
    );
    
  const selectedActions = useMemo(() => {
    return {
      delete: {
        dialogTitle: t("client:delete_client"),
        dialogContent: (
          <ConfirmationForm
            itemName={selected[0]?.id}
            itemType={t("client:client")}
            additionalWarning={t("cant_undo")}
          />
        ),
        disabled: disabledStates.delete,
        onDelete: handleDelete,
      }
      
    };
  }, [
    t,
    jobStatuses,
    isLoading,
    disabledStates,
    selected
  ]);

  const onRefetch = useCallback(() => {
    setSelected([]);
    loadJobStatuses();
  }, [loadJobStatuses]);

  return (
    <ActionTable<JobStatus>
      {...{
        columns,
        data,
        isLoading,
        selected,
        onSelect,
        // actions,
        // selectedActions,
        onRefetch,
        renderRowDetails
      }}
    />
  );
};

export default withAuthentication(SyncLogs);
